import { useState, useEffect } from 'react';
import {
  Plus,
  Edit2,
  Trash2,
  Save,
  X,
  Trophy,
  Calendar,
  MapPin,
  Medal,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { adminFetch, getAuthHeaders } from '../../services/adminApi';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Event types for tournaments
const EVENT_TYPES = [
  { value: 'kata', label: 'Kata' },
  { value: 'kumite', label: 'Kumite (Sparring)' },
  { value: 'kobudo', label: 'Kobudo (Weapons)' },
  { value: 'team_kata', label: 'Team Kata' },
  { value: 'other', label: 'Other' },
];

// Place options
const PLACE_OPTIONS = [
  { value: '1', label: '1st Place', color: 'text-yellow-600' },
  { value: '2', label: '2nd Place', color: 'text-gray-500' },
  { value: '3', label: '3rd Place', color: 'text-amber-700' },
  { value: 'participated', label: 'Participated', color: 'text-blue-600' },
];

// ============================================
// Tournament Result Item (for editing individual results)
// ============================================
const TournamentResultItem = ({ result, index, onUpdate, onRemove }) => {
  return (
    <div className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-800 rounded border">
      <div className="flex-1 grid grid-cols-3 gap-2">
        <select
          value={result.event_type}
          onChange={(e) => onUpdate(index, { ...result, event_type: e.target.value })}
          className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-background text-foreground"
        >
          {EVENT_TYPES.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <Input
          value={result.division || ''}
          onChange={(e) => onUpdate(index, { ...result, division: e.target.value })}
          placeholder="Division (e.g., Youth 10-12)"
          className="text-sm"
        />
        <select
          value={result.place || ''}
          onChange={(e) => onUpdate(index, { ...result, place: e.target.value })}
          className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-background text-foreground"
        >
          <option value="">Select Place</option>
          {PLACE_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => onRemove(index)}
        className="text-red-500 hover:text-red-700"
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
};

// ============================================
// Tournament Form Modal
// ============================================
const TournamentForm = ({ student, tournament, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: tournament?.name || '',
    date: tournament?.date || new Date().toISOString().split('T')[0],
    location: tournament?.location || '',
    organization: tournament?.organization || '',
    results: tournament?.results || [],
    notes: tournament?.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [recentTournaments, setRecentTournaments] = useState([]);
  const [loadingRecent, setLoadingRecent] = useState(true);

  // Fetch recent tournaments for dropdown
  useEffect(() => {
    const fetchRecentTournaments = async () => {
      try {
        const response = await adminFetch(`${API_BASE}/api/admin/tournaments/recent`, {
          headers: getAuthHeaders()
        });
        if (response.ok) {
          const data = await response.json();
          setRecentTournaments(data);
        }
      } catch (error) {
        console.error('Failed to fetch recent tournaments:', error);
      } finally {
        setLoadingRecent(false);
      }
    };
    fetchRecentTournaments();
  }, []);

  // Handle selecting a recent tournament to pre-fill form
  const handleSelectRecentTournament = (e) => {
    const selectedName = e.target.value;
    if (!selectedName) return;

    const selected = recentTournaments.find(t => t.name === selectedName);
    if (selected) {
      setFormData(prev => ({
        ...prev,
        name: selected.name,
        date: selected.date || prev.date,
        location: selected.location || '',
        organization: selected.organization || '',
      }));
    }
  };

  const handleAddResult = () => {
    setFormData({
      ...formData,
      results: [
        ...formData.results,
        { event_type: 'kata', division: '', place: '' }
      ]
    });
  };

  const handleUpdateResult = (index, updatedResult) => {
    const newResults = [...formData.results];
    newResults[index] = updatedResult;
    setFormData({ ...formData, results: newResults });
  };

  const handleRemoveResult = (index) => {
    setFormData({
      ...formData,
      results: formData.results.filter((_, i) => i !== index)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const url = tournament?.id
        ? `${API_BASE}/api/admin/tournaments/${tournament.id}`
        : `${API_BASE}/api/admin/students/${student.id}/tournaments`;

      const response = await fetch(url, {
        method: tournament?.id ? 'PUT' : 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const savedTournament = await response.json();
        onSave(savedTournament);
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to save tournament');
      }
    } catch (error) {
      console.error('Failed to save tournament:', error);
      alert('Failed to save tournament');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="border-2 border-purple-200">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <Trophy className="h-5 w-5 text-purple-600" />
          {tournament?.id ? 'Edit Tournament' : 'Add Tournament Result'}
        </CardTitle>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Recording tournament for: <strong>{student.name}</strong>
        </p>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Recent Tournaments Dropdown */}
            {!tournament?.id && recentTournaments.length > 0 && (
              <div className="md:col-span-2">
                <Label htmlFor="recent">Select from Recent Tournaments</Label>
                <select
                  id="recent"
                  onChange={handleSelectRecentTournament}
                  className="w-full mt-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 bg-purple-50 dark:bg-purple-900/30 text-foreground"
                  defaultValue=""
                >
                  <option value="">-- Or enter new tournament below --</option>
                  {recentTournaments.map((t, idx) => (
                    <option key={idx} value={t.name}>
                      {t.name} ({t.date ? new Date(t.date).toLocaleDateString() : 'No date'})
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Tournament Name */}
            <div className="md:col-span-2">
              <Label htmlFor="name">Tournament Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Florida State Karate Championships"
                className="mt-1"
                required
              />
            </div>

            {/* Date */}
            <div>
              <Label htmlFor="date">Date *</Label>
              <Input
                id="date"
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                max={new Date().toISOString().split('T')[0]}
                className="mt-1"
                required
              />
            </div>

            {/* Location */}
            <div>
              <Label htmlFor="location">Location</Label>
              <Input
                id="location"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder="e.g., Tampa Convention Center"
                className="mt-1"
              />
            </div>

            {/* Organization */}
            <div className="md:col-span-2">
              <Label htmlFor="organization">Organization</Label>
              <Input
                id="organization"
                value={formData.organization}
                onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
                placeholder="e.g., USA Karate, ISKF, etc."
                className="mt-1"
              />
            </div>

            {/* Results Section */}
            <div className="md:col-span-2">
              <div className="flex items-center justify-between mb-2">
                <Label>Event Results</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAddResult}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Event
                </Button>
              </div>

              {formData.results.length === 0 ? (
                <div className="text-center py-4 bg-gray-50 dark:bg-gray-800 rounded border border-dashed text-gray-500 text-sm">
                  No events added yet. Click "Add Event" to record results.
                </div>
              ) : (
                <div className="space-y-2">
                  {formData.results.map((result, index) => (
                    <TournamentResultItem
                      key={index}
                      result={result}
                      index={index}
                      onUpdate={handleUpdateResult}
                      onRemove={handleRemoveResult}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Notes */}
            <div className="md:col-span-2">
              <Label htmlFor="notes">Notes</Label>
              <textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Any additional notes about this tournament..."
                className="w-full mt-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 bg-background text-foreground"
                rows={2}
              />
            </div>
          </div>

          <div className="flex space-x-2 mt-4">
            <Button type="submit" disabled={saving} variant="adminPrimary">
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : (tournament?.id ? 'Update' : 'Save Tournament')}
            </Button>
            <Button type="button" variant="outline" onClick={onCancel}>
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

// ============================================
// Tournament List Item
// ============================================
const TournamentListItem = ({ tournament, onEdit, onDelete, expanded, onToggle }) => {
  const medalCount = tournament.results?.filter(r => ['1', '2', '3'].includes(r.place)).length || 0;

  const getPlaceIcon = (place) => {
    switch (place) {
      case '1': return <Medal className="h-4 w-4 text-yellow-500" />;
      case '2': return <Medal className="h-4 w-4 text-gray-400" />;
      case '3': return <Medal className="h-4 w-4 text-amber-600" />;
      default: return null;
    }
  };

  return (
    <div className="bg-background rounded border hover:shadow-sm transition-shadow">
      <div
        className="flex items-center gap-4 p-3 cursor-pointer"
        onClick={onToggle}
      >
        <div className="p-2 rounded-full bg-purple-100">
          <Trophy className="h-5 w-5 text-purple-600" />
        </div>
        <div className="flex-1">
          <div className="font-medium">{tournament.name}</div>
          <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
            <span className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              {new Date(tournament.date).toLocaleDateString()}
            </span>
            {tournament.location && (
              <span className="flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                {tournament.location}
              </span>
            )}
          </div>
        </div>
        {medalCount > 0 && (
          <div className="flex items-center gap-1 px-2 py-1 bg-yellow-50 dark:bg-yellow-900/30 rounded text-sm">
            <Medal className="h-4 w-4 text-yellow-600" />
            <span className="font-medium">{medalCount}</span>
          </div>
        )}
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); onEdit(tournament); }}>
            <Edit2 className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); onDelete(tournament); }}>
            <Trash2 className="h-4 w-4 text-red-500" />
          </Button>
          {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </div>
      </div>

      {expanded && tournament.results?.length > 0 && (
        <div className="px-4 pb-3 border-t">
          <div className="pt-3 space-y-2">
            <p className="text-xs font-medium text-gray-500 uppercase">Results</p>
            {tournament.results.map((result, idx) => (
              <div key={idx} className="flex items-center gap-3 text-sm">
                {getPlaceIcon(result.place)}
                <span className="font-medium">
                  {EVENT_TYPES.find(e => e.value === result.event_type)?.label || result.event_type}
                </span>
                {result.division && (
                  <span className="text-gray-500 dark:text-gray-400">- {result.division}</span>
                )}
                <span className={`ml-auto font-medium ${
                  PLACE_OPTIONS.find(p => p.value === result.place)?.color || ''
                }`}>
                  {PLACE_OPTIONS.find(p => p.value === result.place)?.label || result.place}
                </span>
              </div>
            ))}
          </div>
          {tournament.notes && (
            <p className="mt-2 text-sm text-gray-500 italic">{tournament.notes}</p>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================
// Main Tournament Editor Component
// ============================================
const TournamentEditor = ({ student, onStudentUpdated }) => {
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingTournament, setEditingTournament] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    if (student?.id) {
      fetchTournaments();
    }
  }, [student?.id]);

  const fetchTournaments = async () => {
    try {
      const response = await adminFetch(`${API_BASE}/api/admin/students/${student.id}/tournaments`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setTournaments(data);
      }
    } catch (error) {
      console.error('Failed to fetch tournaments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (savedTournament) => {
    await fetchTournaments();
    setShowForm(false);
    setEditingTournament(null);
    if (onStudentUpdated) {
      onStudentUpdated();
    }
  };

  const handleDelete = async (tournament) => {
    if (!window.confirm('Are you sure you want to delete this tournament record?')) return;

    try {
      const response = await adminFetch(`${API_BASE}/api/admin/tournaments/${tournament.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        await fetchTournaments();
      }
    } catch (error) {
      console.error('Failed to delete tournament:', error);
    }
  };

  const startEdit = (tournament) => {
    setEditingTournament(tournament);
    setShowForm(true);
  };

  const startAdd = () => {
    setEditingTournament(null);
    setShowForm(true);
  };

  // Calculate stats
  const totalMedals = tournaments.reduce((sum, t) =>
    sum + (t.results?.filter(r => ['1', '2', '3'].includes(r.place)).length || 0), 0
  );
  const goldMedals = tournaments.reduce((sum, t) =>
    sum + (t.results?.filter(r => r.place === '1').length || 0), 0
  );
  const silverMedals = tournaments.reduce((sum, t) =>
    sum + (t.results?.filter(r => r.place === '2').length || 0), 0
  );
  const bronzeMedals = tournaments.reduce((sum, t) =>
    sum + (t.results?.filter(r => r.place === '3').length || 0), 0
  );

  if (!student) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
        Select a student to manage their tournament records.
      </div>
    );
  }

  if (loading) {
    return <div className="text-center py-4">Loading tournaments...</div>;
  }

  return (
    <div className="space-y-4">
      {/* Stats Display */}
      <div className="grid grid-cols-4 gap-2 p-4 bg-gradient-to-r from-purple-50 to-purple-100 dark:from-purple-900/30 dark:to-purple-800/30 rounded-lg">
        <div className="text-center">
          <p className="text-2xl font-bold text-purple-900">{tournaments.length}</p>
          <p className="text-xs text-purple-700">Tournaments</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-yellow-600">{goldMedals}</p>
          <p className="text-xs text-yellow-700">Gold</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-gray-500 dark:text-gray-400">{silverMedals}</p>
          <p className="text-xs text-gray-600 dark:text-gray-400">Silver</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-amber-700">{bronzeMedals}</p>
          <p className="text-xs text-amber-800">Bronze</p>
        </div>
      </div>

      {/* Add Button */}
      {!showForm && (
        <Button onClick={startAdd} variant="adminPrimary">
          <Plus className="h-4 w-4 mr-2" />
          Add Tournament
        </Button>
      )}

      {/* Tournament Form */}
      {showForm && (
        <TournamentForm
          key={editingTournament?.id || 'new'}
          student={student}
          tournament={editingTournament}
          onSave={handleSave}
          onCancel={() => { setShowForm(false); setEditingTournament(null); }}
        />
      )}

      {/* Tournament History */}
      {!showForm && (
        <div className="space-y-2">
          <h4 className="font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
            <Trophy className="h-4 w-4" />
            Tournament History ({tournaments.length})
          </h4>
          {tournaments.length === 0 ? (
            <div className="text-center py-6 text-gray-500 bg-gray-50 dark:bg-gray-800 rounded">
              No tournament records yet.
            </div>
          ) : (
            <div className="space-y-2">
              {tournaments.map(tournament => (
                <TournamentListItem
                  key={tournament.id}
                  tournament={tournament}
                  onEdit={startEdit}
                  onDelete={handleDelete}
                  expanded={expandedId === tournament.id}
                  onToggle={() => setExpandedId(expandedId === tournament.id ? null : tournament.id)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TournamentEditor;
